// services/BaseStrapiService.js
import qs from "qs";
import api, {getAuthToken, normalizeApiError} from "../../system/api";
import {getStorage, getUser} from "../../system/storage";


const waitForWorkspace = () => new Promise((resolve) => {
    const resolveWhenReady = () => {
        const workspaceId = getStorage("workspace");
        if (!workspaceId) return;
        window.removeEventListener("storage", resolveWhenReady);
        resolve(workspaceId);
    };

    window.addEventListener("storage", resolveWhenReady);
    resolveWhenReady();
});

export default class BaseStrapiCollection {
    constructor(kind, singularName, pluralName, {workspaceScoped = true} = {}) {
        this.kind = kind;
        this.singularName = singularName;
        this.pluralName = pluralName;
        this.workspaceScoped = workspaceScoped;

    }

    async waitForWorkspace(workspaceDocumentIdOverride) {
        if (!this.workspaceScoped || workspaceDocumentIdOverride) return;
        await waitForWorkspace();
    }

    getHeader(workspaceDocumentIdOverride){
        const workspaceId = workspaceDocumentIdOverride || getStorage("workspace");
        const user = getUser();
        const token = getAuthToken(user);
        return {
            headers: {
                workspace: workspaceId,
                ...(token ? {Authorization:`Bearer ${token}`} : {}),
            },

        };
    }

    // GET all or GET with filters
    async fetch(query = {}) {
        const url = `/${this.kind==="singleType"?this.singularName:this.pluralName}`;
        // console.log(query)
        try {
            await this.waitForWorkspace();
            const res = await api.get(`${url}?${query??""}`, this.getHeader());
            return res.data;
        } catch (err) {
            console.log(err)
            throw this._normalizeError(err);
        }
    }

    // GET all or GET with filters
    async action(query = {},  url = `/${this.kind==="singleType"?this.singularName:this.pluralName}`, workspaceDocumentIdOverride) {
        // console.log(query)
        try {
            await this.waitForWorkspace(workspaceDocumentIdOverride);
            const res = await api.get(`${url}?${query??""}`, this.getHeader(workspaceDocumentIdOverride));
            return res.data;
        } catch (err) {
            console.log(err)
            throw this._normalizeError(err);
        }
    }

    // GET by ID
    async find(id, query = {}) {
        const url = `/${this.pluralName}`;
        const queryString = typeof query === "string"
            ? query
            : qs.stringify(query ?? {}, {
                encodeValuesOnly: true,
            });
        try {
            await this.waitForWorkspace();
            const res = await api.get(`${url}/${id}?${queryString}`, this.getHeader());
            return res.data;
        } catch (err) {
            throw this._normalizeError(err);
        }
    }

    async postAction(url, data = {}, query = {}, workspaceDocumentIdOverride) {
        try {
            await this.waitForWorkspace(workspaceDocumentIdOverride);
            const res = await api.post(url, {data}, {params: query, ...this.getHeader(workspaceDocumentIdOverride)});
            return res.data;
        } catch (err) {
            throw this._normalizeError(err);
        }
    }

    // options: extra axios config, e.g. {suppressGlobalErrorHandler: true} for background writes
    async putAction(url, data = {}, query = {}, options = {}) {
        try {
            await this.waitForWorkspace();
            const res = await api.put(url, {data}, {params: query, ...this.getHeader(), ...options});
            return res.data;
        } catch (err) {
            throw this._normalizeError(err);
        }
    }

    // POST create
    async create(data, query) {
        const url = `/${this.pluralName}`;
        try {
            await this.waitForWorkspace();
            const res = await api.post(url, { data }, {params:query, ...this.getHeader()});
            return res.data;
        } catch (err) {
            throw this._normalizeError(err);
        }
    }

    // PUT entire update
    async update(id, data, query, workspaceDocumentIdOverride, options = {}) {
        const url = `/${this.pluralName}`;
        try {
            await this.waitForWorkspace(workspaceDocumentIdOverride);
            const res = await api.put(`${url}/${id}`, { data}, {params:query, ...this.getHeader(workspaceDocumentIdOverride), ...options});
            return res.data;
        } catch (err) {
            throw this._normalizeError(err);
        }
    }

    // PATCH partial update
    async patch(id, data) {
        const url = `/${this.pluralName}`;
        try {
            await this.waitForWorkspace();
            const res = await api.patch(`${url}/${id}`, { data }, this.getHeader());
            return res.data;
        } catch (err) {
            throw this._normalizeError(err);
        }
    }

    // DELETE
    async delete(id) {
        const url = `/${this.pluralName}`;
        try {
            await this.waitForWorkspace();
            const res = await api.delete(`${url}/${id}`, this.getHeader());
            return res.data;
        } catch (err) {
            throw this._normalizeError(err);
        }
    }

    // Central error parsing
    _normalizeError(err) {
        return err?.normalizedError || normalizeApiError(err);
    }
}
